// src/pages/Users/News/EditNewsModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes, FaUpload, FaImage } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { updateNews } from "../../../services/newsServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  uploadNewsPhoto,
  toAbsolutePhotoUrl,
} from "../../../services/newsPhotoServices";

const FALLBACK = "/img/no-image.jpg";

// Quill config (sobre)
const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};
const quillFormats = ["header", "bold", "italic", "underline", "list", "bullet", "link", "clean"];

// Helper cache-busting
const withBust = (url, v) => (url ? `${url}${url.includes("?") ? "&" : "?"}v=${v}` : url);

export default function EditNewsModal({ isOpen, onClose, refreshList, news }) {
  const [formData, setFormData] = useState({
    id: "",
    title: "",
    content: "",
    image_url: "",
    is_active: true,
  });

  const [saving, setSaving] = useState(false);

  // Sous-modale upload
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [outputFormat, setOutputFormat] = useState("webp"); // 'webp' | 'jpg' | 'png'
  const [uploadProgress, setUploadProgress] = useState(0);

  // Aperçu + cache-busting
  const [imgVersion, setImgVersion] = useState(0);

  const dialogRef = useRef(null);

  // État initial dérivé
  const initial = useMemo(
    () => ({
      id: news?.id ?? "",
      title: news?.title ?? "",
      content: news?.content ?? "",
      image_url: news?.image_url ?? "",
      is_active: !!news?.is_active,
    }),
    [news]
  );

  // Hydrate le formulaire quand la news change
  useEffect(() => {
    setFormData(initial);
    setImgVersion((v) => v + 1);
  }, [initial]);

  // Verrouille le scroll + ESC + focus
  useEffect(() => {
    if (!isOpen) {
      // reset sous-modale upload à la fermeture
      setIsUploadOpen(false);
      setSelectedFile(null);
      setUploading(false);
      setUploadProgress(0);
      return;
    }
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      if (isUploadOpen) {
        setIsUploadOpen(false);
        return;
      }
      handleClose();
    };
    window.addEventListener("keydown", onEsc);

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='title']")?.focus();
    }, 0);

    return () => {
      root.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [isOpen, isUploadOpen]);

  const isDirty =
    formData.title !== initial.title ||
    formData.content !== initial.content ||
    formData.image_url !== initial.image_url ||
    formData.is_active !== initial.is_active;

  const handleClose = () => {
    if (saving || uploading) return;
    if (!isDirty || window.confirm("Des modifications ne sont pas enregistrées. Fermer ?")) {
      onClose?.();
    }
  };

  const onBackdrop = (e) => {
    if (e.target !== e.currentTarget) return;
    if (isUploadOpen) return; // ne pas fermer le parent quand la sous-modale est ouverte
    handleClose();
  };

  // Handlers champs
  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };
  const handleContentChange = (value) => setFormData((p) => ({ ...p, content: value }));

  // Enregistrement
  const handleSubmit = async (e) => {
    e.preventDefault();
    const title = (formData.title || "").trim();
    if (!title) {
      toast.warn("Le titre est requis.");
      dialogRef.current?.querySelector("input[name='title']")?.focus();
      return;
    }
    if (!formData.id) {
      toast.error("Identifiant de l’actualité manquant.");
      return;
    }

    try {
      setSaving(true);
      await updateNews({
        id: formData.id,
        title,
        content: formData.content,
        image_url: formData.image_url,
        is_active: formData.is_active ? 1 : 0,
      });
      toast.success("Actualité mise à jour.");
      await refreshList?.();
      onClose?.();
    } catch (err) {
      console.error("Erreur update news:", err);
      toast.error("Erreur lors de la mise à jour de l’actualité.");
    } finally {
      setSaving(false);
    }
  };

  // Upload image (local sharp)
  const coverAbs = toAbsolutePhotoUrl(formData.image_url?.trim() ? formData.image_url : FALLBACK);
  const cover = withBust(coverAbs, imgVersion);

  const onFileChange = (e) => setSelectedFile(e.target.files?.[0] || null);

  const handleUpload = async (e) => {
    e.preventDefault(); // ⚠️ la sous-modale a son propre <form>, on controlle le submit
    if (!formData.id) {
      toast.error("Impossible d’associer l’image : id manquant.");
      return;
    }
    if (!selectedFile) {
      toast.warn("Sélectionnez un fichier image.");
      return;
    }

    // Validation front
    const okType = /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type);
    if (!okType) {
      toast.error("Format d'image non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const updated = await uploadNewsPhoto(formData.id, selectedFile, {
        format: outputFormat,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(p);
        },
      });

      setFormData((p) => ({ ...p, image_url: updated?.image_url || p.image_url }));
      setImgVersion((v) => v + 1);
      toast.success("Image téléversée et optimisée.");

      // Ferme la sous-modale sans fermer la modale parente
      setIsUploadOpen(false);
      setSelectedFile(null);
    } catch (err) {
      console.error("Upload image news:", err);
      toast.error("Erreur lors du téléversement de l’image.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={onBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Modifier une actualité"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-4xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              Modifier l’actualité
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Mettez à jour le titre, le contenu et l’image.
            </p>
          </div>
          <Button
            text=""
            icon={FaTimes}
            hoverLabel="Fermer"
            className="h-9 w-9 !p-0 rounded-xl bg-white/15 hover:bg-white/25 !border-0"
            onClick={(e) => {
              e.preventDefault(); // ⛔ empêche le submit involontaire
              e.stopPropagation();
              handleClose();
            }}
          />
        </div>

        {/* Corps scrollable */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-5"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-[360px,1fr] gap-5">
            {/* Colonne image */}
            <div className="flex flex-col gap-3">
              <div className="relative h-40 sm:h-48 w-full rounded-xl bg-white/70 dark:bg-slate-900/60 ring-2 ring-white dark:ring-slate-800 overflow-hidden flex items-center justify-center shadow">
                <img
                  src={cover}
                  alt="Illustration actualité"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK;
                  }}
                  className="h-full w-full object-contain"
                />
                <span className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full grid place-items-center bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                  <FaImage className="text-xs" />
                </span>
              </div>

              <Button
                text="Téléverser une image"
                icon={FaUpload}
                className="!rounded-xl gap-2"
                onClick={(e) => {
                  // ⛔ ce bouton est "submit" par défaut → empêcher la soumission du form parent
                  e.preventDefault();
                  e.stopPropagation();
                  setIsUploadOpen(true);
                }}
              />

              <input
                type="url"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                placeholder="Rempli automatiquement après upload"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                readOnly
              />
            </div>

            {/* Colonne texte */}
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Titre <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Ex. : Résultats tournoi régional"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  required
                  maxLength={160}
                />
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  {formData.title.length}/160 caractères
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Contenu
                </label>
                <div className="rounded-xl overflow-hidden ring-1 ring-slate-300 dark:ring-slate-700">
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={handleContentChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Rédigez ou mettez à jour l’actualité…"
                    className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/70 dark:bg-slate-900/50 px-3 py-2 w-fit">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="accent-violet-500"
                />
                <span className="text-sm text-slate-800 dark:text-slate-100">
                  Rendre l’actualité visible
                </span>
              </label>
            </div>
          </div>

          {/* Footer sticky */}
          <div className="sticky bottom-0 -mx-4 sm:-mx-6 pt-4">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <Button
                text="Annuler"
                icon={FaTimes}
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
                onClick={(e) => {
                  e.preventDefault(); // ⛔ ne pas soumettre
                  e.stopPropagation();
                  handleClose();
                }}
              />
              <Button
                text={saving ? "Enregistrement…" : "Enregistrer"}
                icon={FaSave}
                className="!rounded-xl gap-2"
                // ⚠️ ne pas ajouter preventDefault ici : on veut soumettre le <form>
              />
            </div>
          </div>
        </form>
      </div>

      {/* Sous-modale upload image (local) */}
      {isUploadOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
            onClick={(e) => e.currentTarget === e.target && setIsUploadOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Téléverser une image"
          >
            <div
              className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800 p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-slate-900 dark:text-white font-semibold mb-3">
                Téléverser une image (optimisée ≤1600px)
              </h4>

              <form onSubmit={handleUpload} className="space-y-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={onFileChange}
                  className="block w-full text-sm text-slate-700 dark:text-slate-200"
                />

                <div className="flex items-center gap-2">
                  <span className="text-sm">Format</span>
                  <select
                    className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-2 py-1 text-sm"
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value)}
                    disabled={uploading}
                  >
                    <option value="webp">WEBP (recommandé)</option>
                    <option value="jpg">JPG</option>
                    <option value="png">PNG</option>
                  </select>
                </div>

                {uploading && (
                  <div>
                    <progress className="progress w-full" value={uploadProgress} max="100" />
                    <div className="text-xs mt-1">{uploadProgress}%</div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2">
                  <Button
                    text="Fermer"
                    icon={FaTimes}
                    className="!rounded-xl"
                    onClick={(e) => {
                      e.preventDefault(); // ⛔ ne pas soumettre ce mini-form
                      e.stopPropagation();
                      setIsUploadOpen(false);
                    }}
                  />
                  <Button
                    text={uploading ? "Transfert…" : "Sauvegarder"}
                    icon={FaSave}
                    className="!rounded-xl"
                    // ⚠️ ce bouton doit soumettre la sous-modale → pas de preventDefault ici
                  />
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>,
    document.body
  );
}
